import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from './Modal';
import ContactForm from './ContactForm';
import SEO from './SEO';

const principles = [
  {
    icon: '🎯',
    title: 'Leave everything more organized than I found it.',
    description: 'I improve the systems and processes around the work, not just the deliverable.'
  },
  {
    icon: '📢',
    title: 'Communicate progress early and often.',
    description: "You should never have to ask me where something stands."
  },
  {
    icon: '📚',
    title: 'Write documentation you can actually use.',
    description: 'Your team should be able to extend my work after I leave. That is the point.'
  },
  {
    icon: '🚀',
    title: 'Under-promise, over-deliver.',
    description: 'I set expectations I am confident I can beat, then beat them.'
  },
  {
    icon: '⏰',
    title: "Respect your team's time.",
    description: "I don't ask questions I can answer myself, and I keep meetings few and short."
  },
  {
    icon: '📊',
    title: 'Deliver measurable outcomes.',
    description: 'We agree up front on the number that has to move, and I track it.'
  }
];

const AboutPage = () => {
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO
        title="About Alek Hagopian"
        description="I'm Alek Hagopian — an AI and data engineer who built and sold an AI company, consulted at Mastercard, and now builds production systems for companies and products of my own."
      />

      <div className="about">
        <div className="about-container">
          <div className="project-breadcrumb">
            <Link to="/">Home</Link>
            <span> / </span>
            <span>About</span>
          </div>

          <div className="about-header">
            <div className="about-header-content">
              <div className="about-overline">ABOUT ME</div>
              <h1>Building AI before it was the whole conversation</h1>
              <div className="about-subtitle">
                A decade in data, eight years in AI, and a habit of shipping things.
              </div>
            </div>
          </div>

          <div className="about-content">
            <div className="about-bio">
              <div className="bio-text">
                <div className="bio-content">
                  <div className="bio-header">
                    <div className="bio-image-container">
                      <div className="bio-image-wrapper">
                        <div className="bio-image-glow"></div>
                        <img src="/alek_hagopian.jpg" alt="Alek Hagopian" className="profile-image" />
                        <div className="bio-image-overlay">
                          <div className="experience-badge">8+ years in AI</div>
                        </div>
                      </div>
                    </div>
                    <div className="bio-header-text">
                      <h3>Alek Hagopian</h3>
                      <div className="bio-title">AI &amp; Data Engineer</div>
                    </div>
                  </div>

                  <p>
                    I built my first AI company in 2018, well before the current wave, and sold it
                    in 2021. Before that I was a Senior Managing Consultant at Mastercard, which is
                    where I learned to translate between what a business wants and what a system
                    can actually do.
                  </p>
                  <p>
                    Since then I've worked independently. Companies bring me in when they have a
                    real problem and need someone who can go the whole distance on it — frame it,
                    build the data infrastructure, train the model, and ship the interface people
                    use. I'd rather deliver one working system than five slide decks about it.
                  </p>
                  <p>
                    In between client work I build my own software. Two of those are live and you
                    can go use them right now: <Link to="/apps">Solidly Finance and Solidly Meet</Link>.
                  </p>

                  <div className="bio-highlights">
                    <div className="highlight-item">
                      <span className="highlight-icon">🚀</span>
                      <span>Founded &amp; sold an AI company</span>
                    </div>
                    <div className="highlight-item">
                      <span className="highlight-icon">⚡</span>
                      <span>8+ years in AI/ML</span>
                    </div>
                    <div className="highlight-item">
                      <span className="highlight-icon">💼</span>
                      <span>Former Mastercard consultant</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bio-credentials">
                <div className="credentials-header">
                  <h4>Track Record</h4>
                </div>
                <div className="credentials-grid">
                  <div className="credential-item">
                    <div className="credential-number">8+</div>
                    <div className="credential-label">years in AI</div>
                  </div>
                  <div className="credential-item">
                    <div className="credential-number">10+</div>
                    <div className="credential-label">years in data</div>
                  </div>
                  <div className="credential-item">
                    <div className="credential-number">5</div>
                    <div className="credential-label">startups</div>
                  </div>
                  <div className="credential-item">
                    <div className="credential-number">3</div>
                    <div className="credential-label">acquisitions</div>
                  </div>
                </div>
                {/* Without this, "3 acquisitions" sits beside a bio about selling one
                    company and reads as a discrepancy rather than a broader claim. */}
                <p className="credentials-note">
                  One of those was a company of my own. The other two were clients I helped
                  build to the point of being acquired.
                </p>
                <div className="credentials-list">
                  <div className="credential-badge">
                    <span className="badge-icon">✓</span>
                    <span>Agentic AI</span>
                  </div>
                  <div className="credential-badge">
                    <span className="badge-icon">✓</span>
                    <span>Machine learning</span>
                  </div>
                  <div className="credential-badge">
                    <span className="badge-icon">✓</span>
                    <span>Data engineering</span>
                  </div>
                  <div className="credential-badge">
                    <span className="badge-icon">✓</span>
                    <span>Enterprise infrastructure</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="taglines-section">
            <div className="taglines-header">
              <div className="taglines-overline">HOW I WORK</div>
              <h3>Six things I hold myself to</h3>
              <div className="taglines-underline"></div>
            </div>
            <div className="taglines-grid">
              {principles.map((principle) => (
                <div key={principle.title} className="tagline-card">
                  <div className="tagline-icon">{principle.icon}</div>
                  <h4>{principle.title}</h4>
                  <div className="tagline-description">{principle.description}</div>
                </div>
              ))}
            </div>
          </div>

          <section className="project-cta">
            <div className="cta-content">
              <h2>Think I'd be useful on your problem?</h2>
              <p>Tell me what you're working on. First conversation is free.</p>
              <div className="cta-buttons">
                <button className="btn btn-primary" onClick={() => setShowContactModal(true)}>
                  Work with me →
                </button>
                <Link to="/work" className="btn btn-secondary">See my work</Link>
              </div>
            </div>
          </section>
        </div>
      </div>

      <Modal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        title="Start a conversation"
      >
        <ContactForm onClose={() => setShowContactModal(false)} />
      </Modal>
    </>
  );
};

export default AboutPage;
